import React from "react";
import { Card, Row, Col, Statistic, Spin, Alert } from "antd";
import {
  DollarSign,
  TrendingUp,
  Users,
  Package,
  Calculator,
  Truck,
} from "lucide-react";
import {
  useGetServiceMetrics,
  useGetPackingMetrics,
} from "~/services/service-metrics";
import { useGetStaffRoles } from "~/services/common";

export const ServiceMetricsOverview: React.FC = () => {
  const { data: serviceMetrics, isLoading: metricsLoading } =
    useGetServiceMetrics();
  const { data: staffRoles, isLoading: staffLoading } = useGetStaffRoles();
  const { data: packingMetrics, isLoading: packingLoading } =
    useGetPackingMetrics();

  const isLoading = metricsLoading || staffLoading || packingLoading;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  const totalServiceMetrics = serviceMetrics?.length || 0;
  const activeServiceMetrics =
    serviceMetrics?.filter((m) => m.isActive)?.length || 0;
  const totalStaffRoles = staffRoles?.items?.length || 0;
  const activeStaffRoles = staffRoles?.items?.length || 0; // All staff roles are considered active
  const totalPackingMetrics = packingMetrics?.length || 0;
  const activePackingMetrics =
    packingMetrics?.filter((p) => p.isActive)?.length || 0;

  // Calculate average rates
  const avgStaffRate =
    (staffRoles?.items || []).reduce((sum, role) => sum + role.hourlyRate, 0) /
      (staffRoles?.items?.length || 1) || 0;
  const avgPackingRate =
    (packingMetrics || []).reduce(
      (sum, metric) => sum + (Number(metric.baseRate) || 0),
      0
    ) / (packingMetrics?.length || 1) || 0;

  const overviewCards = [
    {
      title: "Total Service Metrics",
      value: totalServiceMetrics,
      subtitle: `${activeServiceMetrics} active`,
      icon: <Calculator className="text-blue-600" size={24} />,
      color: "border-blue-200 bg-blue-50",
    },
    {
      title: "Staff Roles",
      value: totalStaffRoles,
      subtitle: `${activeStaffRoles} active`,
      icon: <Users className="text-green-600" size={24} />,
      color: "border-green-200 bg-green-50",
    },
    {
      title: "Packing Metrics",
      value: totalPackingMetrics,
      subtitle: `${activePackingMetrics} active`,
      icon: <Package className="text-purple-600" size={24} />,
      color: "border-purple-200 bg-purple-50",
    },
    {
      title: "Avg Staff Rate",
      value: `$${avgStaffRate.toFixed(2)}`,
      subtitle: "per hour",
      icon: <DollarSign className="text-orange-600" size={24} />,
      color: "border-orange-200 bg-orange-50",
    },
    {
      title: "Avg Packing Rate",
      value: `$${avgPackingRate.toFixed(2)}`,
      subtitle: "per cubic foot",
      icon: <TrendingUp className="text-red-600" size={24} />,
      color: "border-red-200 bg-red-50",
    },
    {
      title: "Service Types",
      value: new Set(serviceMetrics?.map((m) => m.serviceType))?.size || 0,
      subtitle: "different types",
      icon: <Truck className="text-indigo-600" size={24} />,
      color: "border-indigo-200 bg-indigo-50",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <Row gutter={[16, 16]}>
        {overviewCards.map((card, index) => (
          <Col xs={24} sm={12} lg={8} key={index}>
            <Card
              className={`border ${card.color} hover:shadow-md transition-shadow`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-600 mb-1">{card.title}</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {card.value}
                  </div>
                  <div className="text-xs text-gray-500">{card.subtitle}</div>
                </div>
                <div className="flex-shrink-0">{card.icon}</div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Service Types Breakdown */}
      <Card title="Service Types Breakdown" className="shadow-sm">
        <Row gutter={[16, 16]}>
          {serviceMetrics && serviceMetrics.length > 0 ? (
            [
              "collection_delivery",
              "site_service",
              "warehouse",
              "fabrication",
              "packing",
            ].map((type) => {
              const count = serviceMetrics.filter(
                (m) => m.serviceType === type
              ).length;
              const activeCount = serviceMetrics.filter(
                (m) => m.serviceType === type && m.isActive
              ).length;

              return (
                <Col xs={24} sm={12} lg={8} key={type}>
                  <Card size="small" className="text-center">
                    <Statistic
                      title={type.replace("_", " ").toUpperCase()}
                      value={count}
                      suffix={`(${activeCount} active)`}
                    />
                  </Card>
                </Col>
              );
            })
          ) : (
            <Col span={24}>
              <Alert
                message="No service metrics found"
                description="Start by creating your first service metric."
                type="info"
                showIcon
              />
            </Col>
          )}
        </Row>
      </Card>

      {/* Recent Activity */}
      <Card title="Quick Stats" className="shadow-sm">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Statistic
              title="Total Service Configurations"
              value={totalServiceMetrics}
              prefix={<Calculator size={16} />}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="Staff Roles Configured"
              value={totalStaffRoles}
              prefix={<Users size={16} />}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="Packing Types Available"
              value={totalPackingMetrics}
              prefix={<Package size={16} />}
            />
          </Col>
        </Row>
      </Card>
    </div>
  );
};
